import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from 'ensightful-koala-ui';
import styled, { css } from 'styled-components';
import Picker from 'emoji-picker-react';
import { Popover } from 'antd';

import Spinner from 'components/atoms/Loading/Spinner';
import { ButtonText } from 'components/atoms/Text';
import { IconButton, TextButton } from 'components/molecules/Button';
import { handleKeyPress } from 'utils/helper';

const ChatInputContainer = styled.div`
    padding: 8px 16px 8px 16px;
    width: 100%;
    height: fit-content;
    border: 1px solid #9e9e9e;
    border-radius: 12px;
    background-color: white;
`;

const ChatInput = styled.input`
    width: 100%;
    border: 0px;
    width: 100%;
`;

const BottomContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 36px;
`;

const SendContainer = styled.div`
    float: right;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const Divider = styled.div`
    width: 100%;
    height: 1px;
    background-color: #e0e0e0;
    margin: auto;
    margin-bottom: 8px;
    margin-top: 8px;
`;

const OptionContainer = styled.div`
    height: 100%;
    align-items: center;
    display: flex;
`;

const UploadButton = styled(IconButton)`
    background-color: transparent;
    padding: 0;

    &:not(:first-child) {
        margin-left: 10px;
    }

    & > svg {
        width: 16px;
        height: 16px;

        & > path {
            fill: #212121;
        }
    }

    ${(props) =>
        props.disabled &&
        css`
            && {
                cursor: default;
            }

            & > svg > path {
                fill: #bdbdbd;
            }
        `}
`;

const SendButton = styled.button`
    border: none;
    border-radius: 4px;
    width: fit-content;
    height: 36px;
    padding: 0px 12px 0px 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #bdbdbd;

    & svg path {
        fill: white;
    }

    &:hover {
        cursor: ${(props) => (props.canSend ? 'pointer' : 'default')};
    }

    ${(props) =>
        props.canSend &&
        css`
            background-color: #6347ff;

            & svg path {
                fill: white;
            }

            & span {
                color: white;
            }
        `};
`;

const ReplyingMessageContainer = styled.div`
    display: flex;
    margin-bottom: 6px;
    min-width: 0;

    & svg path {
        fill: #757575;
    }
`;

const FileList = styled.div`
    display: flex;
    flex-direction: column;
`;

const FileItem = styled.div`
    width: fit-content;
    height: fit-content;
    display: flex;
    align-items: center;
    margin-bottom: 6px;
    padding: 4px 8px 6px 8px;
    color: #212121;
    background-color: #f5f5f5;
    border-radius: 6px;
    font-size: 12px;
    word-break: break-word;
`;

const StyledSpinner = styled(Spinner)`
    width: 12px;
    height: 12px;
    margin-left: 10px;
    margin-right: 4px;
    transform: scale(0.6) translateY(-4px);
    &.ant-spin-spinning {
        margin: auto 8px;
    }
`;

const StyledIconButton = styled(IconButton)`
    margin-left: 8px;
    margin-top: 2px;
    background-color: #f5f5f5;
    padding: 0;
`;

const ReplyingMessage = styled.div`
    width: 300px;
    display: inline;
    -webkit-line-clamp: 2;
    text-overflow: ellipsis;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    word-wrap: break-word;
`;

const EmojiButton = styled.button`
    background-color: transparent;
    border: 0;
    height: 20px;
    padding: 0;
    cursor: pointer;
`;

const EmojiContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    margin-left: 10px;
    cursor: pointer;

    ${(props) =>
        props.disabled &&
        css`
            && > span {
                cursor: default;
            }

            & svg path {
                fill: #bdbdbd;
            }
        `}
`;

const InputBoxWrapper = React.forwardRef(
    (
        {
            className,
            disabled,
            maxLength,
            placeholder = 'Enter your message',
            isEditingButtonText = 'Update',
            currentEditingMessage,
            isReplyingButtonText = 'Reply',
            currentReplyingMessage,
            photoSubmit,
            fileSubmit,
            files = [],
            canRemoveFiles,
            removeFile,
            sendMessage,
            editMessage,
            replyMessage,
            onKeyPress,
            onChange,
            cancelReply,
            cancelEdit,
            onEmojiVisible,
        },
        ref
    ) => {
        const photoManager = useRef(null);
        const fileManager = useRef(null);
        const [, updateState] = useState();
        const [emojiVisible, setEmojiVisible] = useState(false);

        const clearFileManagers = () => {
            photoManager.current.value = '';
            fileManager.current.value = '';
        };

        const handleSend = (canSend) => {
            if (!canSend) return;
            if (currentEditingMessage?.id) {
                editMessage();
            } else if (currentReplyingMessage?.id) {
                replyMessage();
            } else {
                sendMessage();
            }
            clearFileManagers();
        };

        const forceUpdate = useCallback(() => updateState({}), []);

        const renderFileName = (file) => {
            if (file.data) {
                return file.active ? file.data.name : 'This file has been deleted.';
            } else {
                return file.name;
            }
        };

        const handleChange = (e) => {
            if (onChange) onChange(e);
            forceUpdate();
        };

        const handleRemoveFile = (file) => {
            removeFile(file);
        };

        const handleCancelReply = () => {
            clearFileManagers();
            if (cancelReply) cancelReply();
        };

        const handleCancelEdit = () => {
            clearFileManagers();
            cancelEdit();
        };

        const handleEmojiVisibleChange = () => {
            setEmojiVisible((prev) => {
                if (onEmojiVisible) onEmojiVisible(!prev);
                return !prev;
            });
        };

        const handleSelectEmoji = (event, emojiObject) => {
            event.preventDefault();
            if (ref?.current) {
                let cursor = ref.current.selectionStart;
                const message = ref.current.value;
                const text = message.slice(0, cursor) + emojiObject.emoji + message.slice(cursor);
                ref.current.value = text;
                handleEmojiVisibleChange();
            }
        };

        const canUploadFile = !disabled && !(files.length > 0 || currentEditingMessage?.id);
        const canSend = files.length
            ? !files.map((file) => file.isUploading || false).reduce((acc, cur) => acc && cur, true)
            : ref?.current?.value;

        useEffect(() => {
            if (!files.length) {
                clearFileManagers();
            }
        }, [files]);

        return (
            <ChatInputContainer className={className}>
                {currentReplyingMessage && (
                    <ReplyingMessageContainer>
                        <Icon kind={'reply'} style={{ marginRight: 9 }} />
                        <ReplyingMessage>{currentReplyingMessage.messageText}</ReplyingMessage>
                    </ReplyingMessageContainer>
                )}
                {Boolean(files.length) && (
                    <FileList>
                        {files.map((file, index) => (
                            <FileItem key={file.key || index}>
                                {renderFileName(file)}
                                {file.isUploading ? (
                                    <StyledSpinner />
                                ) : (
                                    canRemoveFiles && (
                                        <StyledIconButton
                                            kind={'close'}
                                            iconColor={'#212121'}
                                            size={8}
                                            onClick={() => handleRemoveFile(file)}
                                        />
                                    )
                                )}
                            </FileItem>
                        ))}
                    </FileList>
                )}
                <ChatInput
                    id='au_chatInput'
                    ref={ref}
                    disabled={disabled}
                    maxLength={maxLength}
                    placeholder={placeholder}
                    onKeyPress={onKeyPress}
                    onChange={handleChange}
                    autoComplete={'off'}
                />
                <Divider />
                <BottomContainer>
                    <OptionContainer>
                        <UploadButton
                            disabled={!canUploadFile}
                            tabIndex={!canUploadFile ? -1 : 0}
                            kind={'addimage'}
                            size={16}
                            onClick={() => canUploadFile && photoManager.current.click()}
                            onKeyPress={handleKeyPress(() => canUploadFile && photoManager.current.click())}
                        />
                        <UploadButton
                            disabled={!canUploadFile}
                            tabIndex={!canUploadFile ? -1 : 0}
                            kind={'attachment'}
                            size={16}
                            onClick={() => canUploadFile && fileManager.current.click()}
                            onKeyPress={handleKeyPress(() => canUploadFile && fileManager.current.click())}
                        />
                        <EmojiContainer disabled={disabled}>
                            <Popover
                                content={
                                    <Picker
                                        pickerStyle={{
                                            padding: 0,
                                            border: 'none',
                                            boxShadow: 'none',
                                            margin: '-12px -16px',
                                        }}
                                        native
                                        onEmojiClick={handleSelectEmoji}
                                    />
                                }
                                trigger='click'
                                zIndex={1201}
                                disableSkinTonePicker={false}
                                visible={!disabled && emojiVisible}
                                onVisibleChange={handleEmojiVisibleChange}
                            >
                                <EmojiButton>
                                    <Icon kind={'ontrack'} size={20} fill={'#212121'} />
                                </EmojiButton>
                            </Popover>
                        </EmojiContainer>
                    </OptionContainer>
                    <SendContainer>
                        {(currentReplyingMessage || currentEditingMessage?.id) && (
                            <TextButton
                                style={{ color: '#212121' }}
                                onClick={currentReplyingMessage ? handleCancelReply : handleCancelEdit}
                            >
                                Cancel
                            </TextButton>
                        )}
                        <SendButton
                            id={'au_messageSend'}
                            aria-disabled={!canSend}
                            canSend={canSend}
                            onClick={() => handleSend(canSend)}
                        >
                            {currentEditingMessage?.id ? (
                                <ButtonText style={{ color: 'white' }}>{isEditingButtonText}</ButtonText>
                            ) : currentReplyingMessage ? (
                                <ButtonText style={{ color: 'white' }}>{isReplyingButtonText}</ButtonText>
                            ) : (
                                <Icon kind={'send'} />
                            )}
                        </SendButton>
                    </SendContainer>
                </BottomContainer>
                <input
                    id='photo'
                    style={{ display: 'none' }}
                    ref={photoManager}
                    type='file'
                    accept='image/jpeg, image/gif, image/jpg, image/png, image/svg'
                    onChange={photoSubmit}
                />
                <input id='file' style={{ display: 'none' }} ref={fileManager} type='file' onChange={fileSubmit} />
            </ChatInputContainer>
        );
    }
);

export default InputBoxWrapper;
